import { useEffect, useRef, useState } from "react";
import { useDrawingStore } from "@/stores/drawingStore";

interface PillState {
  x: number;
  y: number;
  price: string;
  time: string;
}

export function DrawingCursorPill() {
  const activeTool = useDrawingStore((s) => s.activeTool);
  const [pill, setPill] = useState<PillState | null>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (!activeTool) {
      setPill(null);
      return;
    }

    const container = document.getElementById("desktop-chart-surface");
    if (!container) return;

    const handleMove = (e: MouseEvent) => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        const chart = (window as any).__LYTICK_CHART__;
        if (!chart) return;

        const rect = container.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        try {
          const result = chart.convertToValue({ x, y });
          if (!result || result.value == null || result.timestamp == null) return;

          const price = result.value.toFixed(result.value < 10 ? 5 : 2);
          const date = new Date(result.timestamp);
          const time = date.toLocaleTimeString(undefined, {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          });

          setPill({ x, y, price, time });
        } catch {
          // chart not ready
        }
      });
    };

    const handleLeave = () => setPill(null);

    container.addEventListener("mousemove", handleMove);
    container.addEventListener("mouseleave", handleLeave);
    return () => {
      container.removeEventListener("mousemove", handleMove);
      container.removeEventListener("mouseleave", handleLeave);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [activeTool]);

  if (!activeTool || !pill) return null;

  const PILL_WIDTH = 130;
  const PILL_HEIGHT = 24;
  const OFFSET_X = 14;
  const OFFSET_Y = -32;

  const container = document.getElementById("desktop-chart-surface");
  const containerWidth = container?.clientWidth ?? 0;
  const containerHeight = container?.clientHeight ?? 0;

  let left = pill.x + OFFSET_X;
  let top = pill.y + OFFSET_Y;
  if (left + PILL_WIDTH > containerWidth - 8) left = pill.x - PILL_WIDTH - 8;
  if (top < 8) top = pill.y + 14;
  if (top + PILL_HEIGHT > containerHeight - 8) top = containerHeight - PILL_HEIGHT - 8;

  return (
    <div
      aria-hidden="true"
      style={{
        position: "absolute",
        left,
        top,
        pointerEvents: "none",
        zIndex: 40,
        display: "flex",
        alignItems: "center",
        gap: 6,
        height: PILL_HEIGHT,
        paddingInline: 8,
        borderRadius: 6,
        backgroundColor: "var(--bg-elevated)",
        border: "1px solid var(--border-default)",
        boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
        backdropFilter: "blur(8px)",
        whiteSpace: "nowrap",
      }}
    >
      <span style={{ fontSize: 11, fontWeight: 600, color: "var(--accent-primary)" }}>
        {pill.price}
      </span>
      <span style={{ fontSize: 11, color: "var(--text-muted)", borderLeft: "1px solid var(--border-muted)", paddingLeft: 6 }}>
        {pill.time}
      </span>
    </div>
  );
}
